// Utility functions for image processing and compression

/**
 * Process an image based on file type and size:
 * - GIFs are preserved as-is (if under 2MB) when uploaded directly
 * - GIFs that are edited will have only the first frame preserved (static image)
 * - PNG/JPG/JPEG/WebP under 150KB are preserved as-is
 * - Images over 150KB are first resized if dimensions are large
 * - If still over 150KB, adaptive compression is applied
 *
 * @param file Original file
 * @returns Promise resolving to processed File object
 */
export const processImageForUpload = async (file: File): Promise<File> => {
    const SIZE_THRESHOLD = 150 * 1024; // 150KB in bytes
    const MAX_SIZE = 2 * 1024 * 1024; // 2MB
    const MAX_DIMENSION = 1200; // Maximum dimension for any image

    // File size validation
    if (file.size > MAX_SIZE) {
        throw new Error(`File exceeds maximum size of 2MB (${Math.round(file.size / 1024)}KB)`);
    }

    // Special handling for GIFs - preserve as-is if uploading directly
    if (file.type === 'image/gif') {
    // Note: At this point, if the user chose to edit a GIF, it will already have been
    // converted to a static image by the editor. If they chose to upload directly,
    // we preserve the full animated GIF.
        return file;
    }

    // For images below threshold, return as-is (no compression needed)
    if (file.size <= SIZE_THRESHOLD) {
        return file;
    }

    // First, resize large images to reasonable dimensions
    const resizedFile = await resizeImageIfNeeded(file, MAX_DIMENSION);

    // If resizing alone got us under threshold, return that
    if (resizedFile.size <= SIZE_THRESHOLD) {
        return resizedFile;
    }

    // Start with higher quality compression after resizing
    let compressedFile = await compressImage(resizedFile, 0.7);

    // If still above threshold, apply adaptive compression
    if (compressedFile.size > SIZE_THRESHOLD) {
        compressedFile = await adaptiveCompress(compressedFile, SIZE_THRESHOLD);
    }

    return compressedFile;
};

/**
 * Resize an image if dimensions exceed maximum allowed
 * Preserves aspect ratio and original format
 *
 * @param file Original image file
 * @param maxDimension Maximum allowed width or height
 * @returns Promise resolving to resized File (or original if no resize needed)
 */
export const resizeImageIfNeeded = async (file: File, maxDimension: number): Promise<File> => {
    return new Promise((resolve, reject) => {
        try {
            const image = new Image();
            image.crossOrigin = 'anonymous';

            const url = URL.createObjectURL(file);

            image.onload = () => {
                try {
                    // Check if resizing is needed
                    if (image.width <= maxDimension && image.height <= maxDimension) {
                        URL.revokeObjectURL(url);
                        resolve(file);
                        return;
                    }

                    // Calculate new dimensions maintaining aspect ratio
                    let newWidth, newHeight;
                    if (image.width > image.height) {
                        newWidth = maxDimension;
                        newHeight = Math.round(image.height * (maxDimension / image.width));
                    } else {
                        newHeight = maxDimension;
                        newWidth = Math.round(image.width * (maxDimension / image.height));
                    }

                    const canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        URL.revokeObjectURL(url);
                        throw new Error('Could not get canvas context');
                    }

                    // Use better quality resizing algorithm
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                    ctx.drawImage(image, 0, 0, newWidth, newHeight);

                    // Keep original format if possible
                    const mimeType = file.type;

                    canvas.toBlob((blob) => {
                        URL.revokeObjectURL(url);

                        if (!blob) {
                            reject(new Error('Canvas to Blob conversion failed'));
                            return;
                        }

                        // Add timestamp to filename for uniqueness
                        const fileNameParts = file.name.split('.');
                        const extension = fileNameParts.pop();
                        const baseName = fileNameParts.join('.');
                        const timestamp = Date.now();
                        const newFileName = `${baseName}_${timestamp}.${extension}`;

                        const resizedFile = new File([blob], newFileName, { type: mimeType });

                        resolve(resizedFile);
                    }, mimeType, 0.95);
                } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            };

            image.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image for resizing'));
            };

            image.src = url;
        } catch (error) {
            reject(error);
        }
    });
};

/**
 * Apply adaptive compression to get file size below target threshold
 * Uses binary search approach to find optimal compression level
 *
 * @param file File to compress
 * @param sizeTarget Target size in bytes
 * @returns Promise resolving to compressed File
 */
export const adaptiveCompress = async (file: File, sizeTarget: number): Promise<File> => {
    // Start with a reasonable quality range
    let minQuality = 0.1;  // Minimum quality (very compressed)
    let maxQuality = 0.7;  // Maximum quality (from initial compression)
    let bestFile = file;
    let attempts = 0;
    const MAX_ATTEMPTS = 5; // Limit attempts to prevent excessive processing

    while (bestFile.size > sizeTarget && attempts < MAX_ATTEMPTS) {
        attempts++;

        // Calculate the midpoint quality to try
        const quality = (minQuality + maxQuality) / 2;

        // Try compressing with this quality level
        const compressedFile = await compressImage(file, quality);

        // Store this as our best result so far
        bestFile = compressedFile;

        // Adjust the range based on results
        if (compressedFile.size > sizeTarget) {
            // Still too large, try lower quality
            maxQuality = quality - 0.01;
        } else {
            // Small enough, but try to improve quality if possible
            minQuality = quality + 0.01;
            // Exit if we're getting very close to the target
            if (sizeTarget - compressedFile.size < 5 * 1024) { // within 5KB
                break;
            }
        }
    }

    return bestFile;
};

/**
 * Compress an image to JPEG with specified quality
 *
 * @param file Original file
 * @param quality Compression quality (0.0-1.0)
 * @returns Promise resolving to compressed File
 */
export const compressImage = async (file: File, quality: number): Promise<File> => {
    return new Promise((resolve, reject) => {
        try {
            // Create an image element to load the file
            const image = new Image();
            image.crossOrigin = 'anonymous';

            // Create URL for the image
            const url = URL.createObjectURL(file);

            image.onload = function() {
                try {
                    // Create canvas with original dimensions
                    const canvas = document.createElement('canvas');
                    canvas.width = image.width;
                    canvas.height = image.height;

                    // Draw the image on canvas
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        URL.revokeObjectURL(url);
                        throw new Error('Could not get canvas context');
                    }

                    ctx.drawImage(image, 0, 0, image.width, image.height);

                    // Convert to JPEG blob with specified quality
                    canvas.toBlob((blob) => {
                        // Always clean up the URL
                        URL.revokeObjectURL(url);

                        if (!blob) {
                            reject(new Error('Canvas to Blob conversion failed'));
                            return;
                        }

                        // Generate a new filename with timestamp for uniqueness
                        const nameWithoutExt = file.name.split('.').slice(0, -1).join('.');
                        const timestamp = Date.now();
                        const newFileName = `${nameWithoutExt}_${timestamp}.jpg`;

                        // Create new File object
                        const compressedFile = new File([blob], newFileName, { type: 'image/jpeg' });

                        resolve(compressedFile);
                    }, 'image/jpeg', quality);
                } catch (error) {
                    URL.revokeObjectURL(url);
                    reject(error);
                }
            };

            image.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image for compression'));
            };

            // Load image from file
            image.src = url;
        } catch (error) {
            reject(error);
        }
    });
};
